import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Logout as LogoutIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  VpnKey as KeyIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Summarize as SummarizeIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Support as SupportIcon,
  BarChart as BarChartIcon,
  Tune as TuneIcon,
} from "@material-ui/icons";
import DashboardLayoutComponent from "./DashboardLayout.component";
import AccessRights from "../../configs/constants/accessRights";
import adminsApi from "../../configs/apis/admins";
import { setAccessRight } from "../../configs/redux/session/session.slice";
import Loading from "../Loading";
import useSupportUnreadPoll from "../../modules/support/hooks/useSupportUnreadPoll";
import { selectSupportTotalUnread } from "../../modules/support/redux/slice/supportNotifications.slice";

// Viewport width below which the sidebar auto-collapses (matches MUI's `lg`
// breakpoint). Session-only — does not overwrite the user's stored preference.
const SIDEBAR_AUTO_COLLAPSE_WIDTH = 1200;

// Ordered to mirror the sidebar: first match wins when the user lacks DASHBOARD_VIEW.
const FALLBACK_LANDING_ROUTES = [
  { right: AccessRights.APPROVAL_VIEW, path: "/admin/approvals" },
  { right: AccessRights.COMPANY_VIEW, path: "/admin/companies" },
  { right: AccessRights.BUNDLE_VIEW, path: "/admin/bundles" },
  { right: AccessRights.COMPANY_CONSUMER_ENGAGEMENT_VIEW, path: "/admin/consumer-engagement" },
  { right: AccessRights.PARTNER_AND_RESELLER_VIEW, path: "/admin/partners-and-resellers" },
  { right: AccessRights.EMAIL_LOG_VIEW, path: "/admin/email-log" },
  { right: AccessRights.MASTER_CONTROL_EDIT, path: "/admin/master-control" },
  { right: AccessRights.DATA_SUMMARY_VIEW, path: "/admin/data-summary" },
  { right: AccessRights.SUPPORT_TICKET_ACCESS, path: "/admin/support" },
  { right: AccessRights.SUPPORT_ANALYTICS_VIEW, path: "/admin/support/analytics" },
  { right: AccessRights.SLA_SETTINGS_VIEW, path: "/admin/support/sla-settings" },
  { right: AccessRights.ADMIN_VIEW, path: "/admin/management-sn" },
];

export default function DashboardLayoutContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const alerts = useSelector((state) => state.alerts.alerts);
  const isAuthenticated = useSelector(
    (state) => state.session.accessToken !== ""
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Persistent sidebar collapse state (desktop). Remembered across reloads.
  // Also auto-collapses when the viewport is narrower than SIDEBAR_AUTO_COLLAPSE_WIDTH.
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem("sidebar_collapsed") === "true";
      const narrow = typeof window !== "undefined"
        && window.innerWidth < SIDEBAR_AUTO_COLLAPSE_WIDTH;
      return stored || narrow;
    } catch (e) {
      return false;
    }
  });

  // Watch viewport: auto-collapse on narrow (session-only, does not overwrite
  // the user's stored preference so wide-screen sessions stay expanded).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      if (window.innerWidth < SIDEBAR_AUTO_COLLAPSE_WIDTH) {
        // Idempotent: React skips the re-render when setting the same value.
        setIsCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accessRights = useSelector((state) => state.session.accessRights);

  const [name, setName] = useState("");
  const [roleName, setRoleName] = useState("");
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [navigate, isAuthenticated]);

  const fetch = () => {
    adminsApi.bootstrap().then(async (response) => {
      setName(response.data.name);
      setRoleName(response.data.role_name);
      const rights = response.data.access_right ?? [];
      await dispatch(setAccessRight(rights));
      if (location.pathname === "/admin" && !rights.includes(AccessRights.DASHBOARD_VIEW)) {
        const landing = FALLBACK_LANDING_ROUTES.find((entry) => rights.includes(entry.right));
        if (landing) {
          navigate(landing.path, { replace: true });
        }
      }
      setIsReady(true);
    });
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetch();
    }
  }, [isAuthenticated]);

  // Support nav unread badge: poll the admin unread-summary endpoint every
  // 30s while the shell is mounted and the admin has Support access.
  const supportUnread = useSelector(selectSupportTotalUnread);
  const hasSupportAccess = accessRights.includes(AccessRights.SUPPORT_TICKET_ACCESS);
  useSupportUnreadPoll(hasSupportAccess);

  const onDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const onCollapseToggle = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("sidebar_collapsed", String(next)); } catch (e) { /* ignore quota / disabled storage */ }
      return next;
    });
  };

  const items = [
    {
      icon: <DashboardIcon />,
      title: "Home",
      enabled: accessRights.includes(AccessRights.DASHBOARD_VIEW),
      active: location.pathname === "/admin",
      onClick: () => navigate("/admin"),
    },
    {
      icon: <CheckCircleOutlineIcon />,
      title: "Approvals",
      active: location.pathname === "/admin/approvals",
      enabled: accessRights.includes(AccessRights.APPROVAL_VIEW),
      onClick: () => navigate("/admin/approvals"),
    },
    {
      icon: <BusinessIcon />,
      title: "Companies",
      active: location.pathname === "/admin/companies",
      enabled: accessRights.includes(AccessRights.COMPANY_VIEW),
      onClick: () => navigate("/admin/companies"),
    },
    {
      icon: <KeyIcon />,
      title: "Access Bundles",
      active: location.pathname === "/admin/bundles",
      enabled: accessRights.includes(AccessRights.BUNDLE_VIEW),
      onClick: () => navigate("/admin/bundles"),
    },
    {
      icon: <PeopleIcon />,
      title: "Consumer Engagement",
      active: location.pathname === "/admin/consumer-engagement",
      enabled: accessRights.includes(
        AccessRights.COMPANY_CONSUMER_ENGAGEMENT_VIEW
      ),
      onClick: () => navigate("/admin/consumer-engagement"),
    },
    {
      icon: <PersonIcon />,
      title: "Partners & Resellers",
      active: location.pathname === "/admin/partners-and-resellers",
      enabled: accessRights.includes(AccessRights.PARTNER_AND_RESELLER_VIEW),
      onClick: () => navigate("/admin/partners-and-resellers"),
    },
    {
      icon: <EmailIcon />,
      title: "Email Log",
      active: location.pathname === "/admin/email-log",
      enabled: accessRights.includes(AccessRights.EMAIL_LOG_VIEW),
      onClick: () => navigate("/admin/email-log"),
    },
    {
      icon: <SettingsIcon />,
      title: "Master Control",
      active: location.pathname === "/admin/master-control",
      enabled: accessRights.includes(AccessRights.MASTER_CONTROL_EDIT),
      onClick: () => navigate("/admin/master-control"),
    },
    {
      icon: <SummarizeIcon />,
      title: "Data Summary",
      active: location.pathname === "/admin/data-summary",
      enabled: accessRights.includes(AccessRights.DATA_SUMMARY_VIEW),
      onClick: () => navigate("/admin/data-summary"),
    },
    (() => {
      const supportChildren = [
        {
          icon: <SupportIcon />,
          title: "Support Tickets",
          active: location.pathname.startsWith("/admin/support") && !location.pathname.includes("analytics") && !location.pathname.includes("sla-settings"),
          enabled: accessRights.includes(AccessRights.SUPPORT_TICKET_ACCESS),
          onClick: () => navigate("/admin/support"),
          // Badge shows on the "Support Tickets" child when the sidebar is
          // expanded, so the user can see the unread count next to the
          // most relevant entry.
          badgeCount: supportUnread,
        },
        {
          icon: <BarChartIcon />,
          title: "Support Analytics",
          active: location.pathname === "/admin/support/analytics",
          enabled: accessRights.includes(AccessRights.SUPPORT_ANALYTICS_VIEW),
          onClick: () => navigate("/admin/support/analytics"),
        },
        {
          icon: <TuneIcon />,
          title: "SLA Settings",
          active: location.pathname === "/admin/support/sla-settings",
          enabled: accessRights.includes(AccessRights.SLA_SETTINGS_VIEW),
          onClick: () => navigate("/admin/support/sla-settings"),
        },
      ].filter((e) => e.enabled);
      return {
        icon: <SupportIcon />,
        title: "Support Module",
        enabled: supportChildren.length > 0,
        children: supportChildren,
        // Also show on the collapsed parent so the badge is visible when
        // the sidebar is in icon-only mode and "Support Tickets" is hidden
        // behind the group header.
        badgeCount: supportUnread,
      };
    })(),
    {
      icon: <ConfirmationNumberIcon />,
      title: "Management SN",
      active: location.pathname === "/admin/management-sn",
      enabled: accessRights.includes(AccessRights.ADMIN_VIEW),
      onClick: () => navigate("/admin/management-sn"),
    },
    {
      icon: <AdminPanelSettingsIcon />,
      title: "Admins",
      active: location.pathname === "/admin/admins",
      enabled: accessRights.includes(AccessRights.ADMIN_VIEW),
      onClick: () => navigate("/admin/admins"),
    },
    {
      icon: <LogoutIcon />,
      title: "Logout",
      active: false,
      enabled: true,
      onClick: () => navigate("/logout"),
    },
  ].filter((e) => e.enabled);

  if (!isReady) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loading />
      </div>
    );
  }

  return (
    <DashboardLayoutComponent
      items={items}
      alerts={alerts}
      isDrawerOpen={isDrawerOpen}
      onDrawerToggle={onDrawerToggle}
      isCollapsed={isCollapsed}
      onCollapseToggle={onCollapseToggle}
      name={name}
      roleName={roleName}
    />
  );
}
